import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IThemeColorsModel } from '../../models/event.page.model';
import { allHeaderTypes } from '../../constants/event-page.constants';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-event-page-custom-theme-configuration',
  templateUrl: './event-page-custom-theme-configuration.component.html',
  styleUrls: ['./event-page-custom-theme-configuration.component.scss']
})
export class EventPageCustomThemeConfigurationComponent implements OnInit, OnDestroy {
  @Input() public selectedTheme: IThemeColorsModel;
  @Input() public themeAction: string;
  @Input() public headerType = 1;
  @Output() public onDeleteTheme = new EventEmitter<any>();
  @Output() public onSaveTheme = new EventEmitter<IThemeColorsModel>();

  public allHeaderTypes = allHeaderTypes
  private takeUntilSubject: Subject<any> = new Subject();

  public themeForm: UntypedFormGroup;

  constructor() {
  }

  ngOnInit(): void {
    if (!this.selectedTheme.generalColors) {
      this.selectedTheme.generalColors = {
        pageBgColor: '#FFFFFF'
      };
    }

    // added to handle already created custom themes jul 3, 2024
    const imageSectionBgColor = this.selectedTheme.imageColors?.imageSectionBgColor || this.selectedTheme.videoColors.videoSectionBgColor;
    const imageSectionTextColor = this.selectedTheme.imageColors?.imageSectionTextColor || this.selectedTheme.videoColors.videoSectionTextColor;

    // added to handle already created custom themes jul 15, 2024
    const sponsorsBgColor = this.selectedTheme.sponsorsColors?.sponsorsBgColor || '#ffffff';
    const sponsorsTitleColor = this.selectedTheme.sponsorsColors?.sponsorsTitleColor || '#000000';
    const sponsorsTextColor = this.selectedTheme.sponsorsColors?.sponsorsTextColor || '#828282';

    this.themeForm = new UntypedFormGroup({
      themeName: new UntypedFormControl(this.selectedTheme.name.trim(), [Validators.required, Validators.minLength(1)]),
      pageBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.generalColors.pageBgColor)),
      navBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.headerColors.navBgColor)),
      navRegularLink: new UntypedFormControl(this.hexToRgb(this.selectedTheme.headerColors.navRegularLinkColor)),
      navActiveLink: new UntypedFormControl(this.hexToRgb(this.selectedTheme.headerColors.navActiveLinkColor)),

      slideShowText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.slideShowColors.slideShowTextColor)),
      slideShowBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.slideShowColors.slideShowBgColor)),

      sponsorsBg: new UntypedFormControl(this.hexToRgb(sponsorsBgColor)),
      sponsorsTitle: new UntypedFormControl(this.hexToRgb(sponsorsTitleColor)),
      sponsorsText: new UntypedFormControl(this.hexToRgb(sponsorsTextColor)),

      auctionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.customThemeAuctionColors.auctionBgColor)),
      auctionText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.customThemeAuctionColors.auctionTextColor)),

      textSectionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.textSectionColors.textSectionBgColor)),
      textSectionText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.textSectionColors.textSectionTextColor)),
      textSectionTitle: new UntypedFormControl(this.hexToRgb(this.selectedTheme.textSectionColors.textSectionTitleColor)),

      faqSectionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.fAQSectionColors.faqSectionBgColor)),
      faqSectionTitle: new UntypedFormControl(this.hexToRgb(this.selectedTheme.fAQSectionColors.faqSectionTitleColor)),
      faqSectionItemBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.fAQSectionColors.faqSectionItemTextBgColor)),
      faqSectionItemText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.fAQSectionColors.faqSectionItemTextColor)),

      donationCampaignSectionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.campaignSectionColors.donationCampaignSectionBgColor)),
      donationCampaignSectionText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.campaignSectionColors.donationCampaignSectionTextColor)),
      donationCampaignButtonBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.campaignSectionColors.donationCampaignButtonBgColor)),
      donationCampaignButtonText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.campaignSectionColors.donationCampaignButtonTextColor)),
      videoSectionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.videoColors.videoSectionBgColor)),
      videoSectionText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.videoColors.videoSectionTextColor)),
      imageSectionBg: new UntypedFormControl(this.hexToRgb(imageSectionBgColor)),
      imageSectionText: new UntypedFormControl(this.hexToRgb(imageSectionTextColor)),

      informationContainerButtonText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerButtonTextColor)),
      informationContainerButtonBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerButtonBgColor)),
      informationContainerDate: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerDateColor)),
      informationContainerShareIconsText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerShareIconsTextColor)),
      informationContainerBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerBg)),
      informationContainerTextColor: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerTextColor)),
      informationContainerRightTriangleBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.informationContainerColors.informationContainerRightTriangleBgColor)),
      headerTheme: new UntypedFormControl(this.headerType),

      footerBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.footerColors.footerBgColor)),
      footerText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.footerColors.footerTextColor)),

      orgInfoSectionBg: new UntypedFormControl(this.hexToRgb(this.selectedTheme.orgInfoColors.orgInfoBgColor)),
      orgInfoSectionText: new UntypedFormControl(this.hexToRgb(this.selectedTheme.orgInfoColors.orgInfoTextColor)),
      headerType: new UntypedFormControl(this.headerType),
    });
  }

  ngOnDestroy(): void {
    this.takeUntilSubject.next(true);
    this.takeUntilSubject.complete();
  }

  deleteTheme(): void {
    this.onDeleteTheme.emit(true);
  }

  saveTheme(): void {
    const payload: IThemeColorsModel = {
      ...this.selectedTheme,
      name: this.themeForm.value.themeName,
      headerType: this.themeForm.value.headerType,
      baseColorTheme: 10,
      designTemplate: 0,
      headerColors: {
        navBgColor: `${this.themeForm.value.navBg}`,
        navRegularLinkColor: `${this.themeForm.value.navRegularLink}`,
        navActiveLinkColor: `${this.themeForm.value.navActiveLink}`,
      },
      sponsorsColors: {
        sponsorsTitleColor: `${this.themeForm.value.sponsorsTitle}`,
        sponsorsTextColor: `${this.themeForm.value.sponsorsText}`,
        sponsorsBgColor: `${this.themeForm.value.sponsorsBg}`,
      },
      customThemeAuctionColors: {
        auctionTextColor: `${this.themeForm.value.auctionText}`,
        auctionBgColor: `${this.themeForm.value.auctionBg}`,
      },
      generalColors: {
        pageBgColor: `${this.themeForm.value.pageBg}`,
      },
      textSectionColors: {
        textSectionTextColor: `${this.themeForm.value.textSectionText}`,
        textSectionBgColor: `${this.themeForm.value.textSectionBg}`,
        textSectionTitleColor: `${this.themeForm.value.textSectionTitle}`,
      },
      fAQSectionColors: {
        faqSectionTitleColor: `${this.themeForm.value.faqSectionTitle}`,
        faqSectionBgColor: `${this.themeForm.value.faqSectionBg}`,
        faqSectionItemTextColor: `${this.themeForm.value.faqSectionItemText}`,
        faqSectionItemTextBgColor: `${this.themeForm.value.faqSectionItemBg}`
      },
      campaignSectionColors: {
        donationCampaignSectionBgColor: `${this.themeForm.value.donationCampaignSectionBg}`,
        donationCampaignSectionTextColor: `${this.themeForm.value.donationCampaignSectionText}`,
        donationCampaignButtonBgColor: `${this.themeForm.value.donationCampaignButtonBg}`,
        donationCampaignButtonTextColor: `${this.themeForm.value.donationCampaignButtonText}`,
      },
      videoColors: {
        videoSectionBgColor: `${this.themeForm.value.videoSectionBg}`,
        videoSectionTextColor: `${this.themeForm.value.videoSectionText}`
      },
      imageColors: {
        imageSectionBgColor: `${this.themeForm.value.imageSectionBg}`,
        imageSectionTextColor: `${this.themeForm.value.imageSectionText}`
      },
      informationContainerColors: {
        informationContainerButtonTextColor: `${this.themeForm.value.informationContainerButtonText}`,
        informationContainerButtonBgColor: `${this.themeForm.value.informationContainerButtonBg}`,
        informationContainerDateColor: `${this.themeForm.value.informationContainerDate}`,
        informationContainerShareIconsTextColor: `${this.themeForm.value.informationContainerShareIconsText}`,
        informationContainerBg: `${this.themeForm.value.informationContainerBg}`,
        informationContainerTextColor: `${this.themeForm.value.informationContainerTextColor}`,
        informationContainerRightTriangleBgColor: `${this.themeForm.value.informationContainerRightTriangleBg}`,
      },
      slideShowColors: {
        slideShowTextColor: `${this.themeForm.value.slideShowText}`,
        slideShowBgColor: `${this.themeForm.value.slideShowBg}`,
      },
      footerColors: {
        footerTextColor: `${this.themeForm.value.footerText}`,
        footerBgColor: `${this.themeForm.value.footerBg}`,
      },
      orgInfoColors: {
        orgInfoBgColor: `${this.themeForm.value.orgInfoSectionBg}`,
        orgInfoTextColor: `${this.themeForm.value.orgInfoSectionText}`,
      },
    }

    this.onSaveTheme.emit(payload);
  }

  onCancel(): void {
    this.onDeleteTheme.emit(true);
  }

  hexToRgb(hex) {
    return hex;
    /*
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result && result.length ?
      new Color(parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16))
      : new Color(255, 255, 255, 1);
    */
  }

  getTriangleStyles() {
    const { value: informationContainerBg }  = this.themeForm.get('informationContainerBg');
    const { value: informationContainerRightTriangleBg } = this.themeForm.get('informationContainerRightTriangleBg');
    return `background: linear-gradient(to bottom right,${informationContainerBg} 0%,${informationContainerBg} 50%,${informationContainerRightTriangleBg} 50%,${informationContainerRightTriangleBg} 100%)`;
  }

  identify(index, item){
    return item.name;
  }
}
